/**
 * Composable for working with Google Tag Manager (gtag)
 * Provides type-safe methods for sending events to Google Analytics
 */
export function useGtag() {
  const { gtagConversionId, gtagConversionIdRegistration, gtagConversionIdFirstDeposit } = useRuntimeConfig().public;

  /**
   * Send an event to Google Analytics
   * @param eventName - Name of the event (e.g., 'page_view', 'click', 'purchase')
   * @param eventParams - Optional parameters for the event
   */
  const sendEvent = (eventName: string, eventParams?: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, eventParams);
    }
  };

  /**
   * Set user properties
   * @param properties - User properties to set
   */
  const setUserProperties = (properties: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('set', 'user_properties', properties);
    }
  };

  /**
   * Set user ID
   * @param userId - User ID to set
   */
  const setUserId = (userId: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', useRuntimeConfig().public.gtagId, {
        user_id: userId,
      });
    }
  };

  const reportGtagConversion = () => {
    if (typeof window.gtag_report_conversion === 'function') {
      window.gtag_report_conversion();
    }
  };

  /**
   * Check if gtag is available
   */
  const isGtagAvailable = () => {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  };

  const initConversionId = () => {
    if (!window.gtag || !gtagConversionId) return;
    window.gtag('config', gtagConversionId);
  };

  const sendConversionRegistration = () => {
    if (!window.gtag || !gtagConversionIdRegistration) return;
    window.gtag('event', 'conversion', { send_to: gtagConversionIdRegistration });
  };

  const sendConversionFirstDeposit = ({
    value,
    currency,
    transaction_id,
  }: {
    value: number;
    currency: string;
    transaction_id: string;
  }) => {
    if (!window.gtag || !gtagConversionIdFirstDeposit) return;
    window.gtag('event', 'conversion', {
      send_to: gtagConversionIdFirstDeposit,
      value,
      currency,
      transaction_id,
    });
  };

  return {
    sendEvent,
    setUserProperties,
    setUserId,
    isGtagAvailable,
    initConversionId,
    sendConversionRegistration,
    sendConversionFirstDeposit,
    reportGtagConversion,
  };
}
